import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EventsService from "../services/EventsService";

const actionsColorMap = [
  "#1E8E3A", "#0A84FF", "#C62828", "#B8960A",
  "#8E44AD", "#E67E22", "#00BCD4", "#E91E63", "#3F51B5", "#7CB342",
];

const CATEGORIES_DATA = [
  { value: "fitness", key: "fitness", icon: "🏋️" },
  { value: "health", key: "health", icon: "❤️" },
  { value: "cooking", key: "cooking", icon: "🍳" },
  { value: "work", key: "work", icon: "💼" },
  { value: "baby", key: "baby", icon: "👶" },
  { value: "fishing", key: "fishing", icon: "🎣" },
  { value: "leisure", key: "leisure", icon: "🎮" },
  { value: "other", key: "other", icon: "📋" },
];

function getCategories(t) {
  return CATEGORIES_DATA.map((c) => ({ ...c, label: t(`categories.${c.key}`) }));
}

function TemplatesPage() {
  const { t } = useTranslation();
  const CATEGORIES = getCategories(t);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [starting, setStarting] = useState(null);
  const navigate = useNavigate();

  // Create form
  const [name, setName] = useState("");
  const [category, setCategory] = useState("other");
  const [icon, setIcon] = useState("📋");
  const [isPublic, setIsPublic] = useState(false);
  const [templateActions, setTemplateActions] = useState([{ name: "", typeId: "custom-0" }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await EventsService.listTemplates();
      setTemplates(data || []);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleQuickStart = async (templateId) => {
    setStarting(templateId);
    try {
      const result = await EventsService.quickStartFromTemplate(templateId);
      if (result?.eventId || result?.id) {
        navigate(`/event/${result.eventId || result.id}`);
      } else {
        navigate("/my-events");
      }
    } catch (err) {
      alert(t("templates.failedToStart", { message: err.message }));
    } finally {
      setStarting(null);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm(t("templates.deleteConfirm"))) return;
    try {
      await EventsService.deleteTemplate(templateId);
      fetchTemplates();
    } catch (err) {
      alert(err.message);
    }
  };

  const addAction = () => {
    const idx = templateActions.length;
    setTemplateActions([...templateActions, { name: "", typeId: `custom-${idx}` }]);
  };

  const removeAction = (i) => {
    const updated = templateActions.filter((_, idx) => idx !== i);
    setTemplateActions(updated.map((a, idx) => ({ ...a, typeId: `custom-${idx}` })));
  };

  const updateActionName = (i, val) => {
    const updated = [...templateActions];
    updated[i].name = val;
    setTemplateActions(updated);
  };

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) {
      setError(t("templates.errTemplateName"));
      return;
    }
    const validActions = templateActions.filter((a) => a.name.trim());
    if (validActions.length === 0) {
      setError(t("templates.errAtLeastOneAction"));
      return;
    }
    setSaving(true);
    try {
      await EventsService.createTemplate({
        name: name.trim(),
        category,
        icon,
        isPublic,
        actions: validActions.map((a) => ({ name: a.name, typeId: a.typeId })),
      });
      setShowCreate(false);
      setName("");
      setCategory("other");
      setIcon("📋");
      setIsPublic(false);
      setTemplateActions([{ name: "", typeId: "custom-0" }]);
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            <a
              id="sidebar-toggle"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.__toggleSidebar && window.__toggleSidebar(e);
              }}
            >
              <i className="fas fa-bars"></i>
            </a>
            <h5>{t("templates.title")}</h5>
          </div>
        </nav>
      </div>
      <div className="main-wrapper">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, margin: 0 }}>
            {t("templates.subtitle")}
          </p>
          <button
            className="btn btn-info"
            onClick={() => setShowCreate(!showCreate)}
          >
            <i className={`fas fa-${showCreate ? "times" : "plus"}`} style={{ marginRight: 6 }}></i>
            {showCreate ? t("common.cancel") : t("templates.createTemplate")}
          </button>
        </div>

        {/* Create Form */}
        {showCreate && (
          <div className="glass-card" style={{ padding: 24, marginBottom: 24 }}>
            <h6 className="event-section-title" style={{ marginBottom: 16 }}>
              {t("templates.newTemplate")}
            </h6>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
              <input
                type="text"
                className="form-control"
                placeholder={t("templates.templateNamePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ flex: "2 1 200px" }}
              />
              <input
                type="text"
                className="form-control"
                placeholder={t("templates.iconPlaceholder")}
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                style={{ flex: "0 0 80px", textAlign: "center", fontSize: 20 }}
              />
              <select
                className="form-control"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{ flex: "1 1 160px" }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
                ))}
              </select>
            </div>

            <label className="alarm-toggle" style={{ marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              <span>{t("templates.makePublicLabel")}</span>
            </label>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, display: "block" }}>
                {t("templates.actionsLabel")}
              </label>
              {templateActions.map((a, i) => (
                <div key={i} className="custom-action-row">
                  <span
                    className="custom-action-dot"
                    style={{ backgroundColor: actionsColorMap[i % actionsColorMap.length] }}
                  />
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("templates.actionPlaceholder", { index: i + 1 })}
                    value={a.name}
                    onChange={(e) => updateActionName(i, e.target.value)}
                  />
                  {templateActions.length > 1 && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeAction(i)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              ))}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={addAction}
                style={{ marginTop: 8 }}
              >
                <i className="fas fa-plus" style={{ marginRight: 6 }}></i>
                {t("templates.addAction")}
              </button>
            </div>

            {error && (
              <div className="alert alert-danger" role="alert" style={{ marginBottom: 12 }}>
                {error}
              </div>
            )}

            <button
              className="btn btn-success"
              onClick={handleCreate}
              disabled={saving}
            >
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        )}

        {/* Templates List */}
        {loading ? (
          <div className="d-flex justify-content-center" style={{ padding: 60 }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t("common.loading")}</span>
            </div>
          </div>
        ) : templates.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <p style={{ fontSize: 48, marginBottom: 8 }}>📋</p>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 16 }}>
              {t("templates.noTemplatesYet")}
            </p>
          </div>
        ) : (
          <div className="templates-grid">
            {templates.map((tpl) => {
              const catInfo = CATEGORIES.find((c) => c.value === tpl.category) || CATEGORIES[CATEGORIES.length - 1];
              return (
                <div key={tpl.id} className="glass-card template-card">
                  <div className="template-card-header">
                    <span className="template-card-icon">{tpl.icon || catInfo.icon}</span>
                    <div>
                      <h6 className="template-card-name">{tpl.name}</h6>
                      <span className="template-card-category">{catInfo.label}</span>
                    </div>
                  </div>
                  <div className="template-card-meta">
                    <span>{tpl.actions?.length || 0} {t("common.actions")}</span>
                    {tpl.usageCount !== undefined && (
                      <span>{tpl.usageCount} {t("common.uses")}</span>
                    )}
                    {tpl.isPublic && <span className="template-card-public">{t("common.public")}</span>}
                  </div>
                  {tpl.actions && tpl.actions.length > 0 && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
                      {tpl.actions.map((a, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 11,
                            padding: "3px 8px",
                            borderRadius: 6,
                            background: `${actionsColorMap[i % actionsColorMap.length]}22`,
                            color: actionsColorMap[i % actionsColorMap.length],
                            fontWeight: 600,
                          }}
                        >
                          {a.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => handleQuickStart(tpl.id)}
                      disabled={starting === tpl.id}
                      style={{ flex: 1 }}
                    >
                      {starting === tpl.id ? (
                        <span className="spinner-border spinner-border-sm" style={{ marginRight: 6 }} />
                      ) : (
                        <i className="fas fa-play" style={{ marginRight: 6 }}></i>
                      )}
                      {t("templates.quickStart")}
                    </button>
                    {tpl.isOwn !== false && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(tpl.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default TemplatesPage;
